import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Plus, CheckCircle2, Circle, AlertCircle, Calendar, Users, DollarSign, ClipboardCheck, FileText, ListChecks, GanttChart, Activity as ActivityIcon, Trash2, X, Stamp, ClipboardList } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { FilesTab as RealFilesTab, PermitsTab, InspectionsTab } from "./tabs/P4ATabs";
import { FieldTab, CoordinationTab, CapitalTab } from "./tabs/P4BCDTabs";
import { AIInsightsPanel, ReportsTab } from "./tabs/P5Tabs";

const NAVY = "#0F2747";
const ICE = "#DCEEFF";
const STEEL = "#5B7FB0";

const CURRENCY = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const TASK_STATUSES = ["not_started", "in_progress", "blocked", "completed"];
const TASK_STATUS_COLORS = {
    not_started: "text-muted-foreground",
    in_progress: "text-steel",
    blocked: "text-destructive",
    completed: "text-emerald-500",
};

const CHECKLIST_STATUSES = [
    "not_started", "in_progress", "pending_docs", "pending_approval",
    "under_review", "returned_for_edits", "complete", "verified", "waived", "na",
];

const TABS = [
    { key: "overview", label: "Overview", icon: LayoutIcon },
    { key: "roadmap", label: "Roadmap", icon: GanttChart },
    { key: "checklist", label: "Checklist", icon: ClipboardCheck },
    { key: "tasks", label: "Tasks", icon: ListChecks },
    { key: "schedule", label: "Schedule", icon: Calendar },
    { key: "field", label: "Field", icon: ClipboardList },
    { key: "budget", label: "Budget", icon: DollarSign },
    { key: "capital", label: "Capital", icon: DollarSign },
    { key: "coordination", label: "Coordination", icon: FileText },
    { key: "reports", label: "Reports", icon: FileText },
    { key: "permits", label: "Permits", icon: Stamp },
    { key: "inspections", label: "Inspections", icon: ClipboardList },
    { key: "team", label: "Team", icon: Users },
    { key: "files", label: "Files", icon: FileText },
    { key: "activity", label: "Activity", icon: ActivityIcon },
];

function LayoutIcon(props) { return <ClipboardCheck {...props} />; }

export default function ProjectWorkspace() {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [phases, setPhases] = useState([]);
    const [tab, setTab] = useState("overview");

    const loadProject = useCallback(async () => {
        try {
            const [{ data: p }, { data: ph }] = await Promise.all([
                api.get(`/projects/${id}`),
                api.get(`/projects/${id}/phases`),
            ]);
            setProject(p);
            setPhases(ph.items || []);
        } catch { toast.error("Failed to load project"); }
    }, [id]);
    useEffect(() => { loadProject(); }, [loadProject]);

    if (!project) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="p-6 md:p-10" data-testid="page-project-workspace">
            <Link to="/app/projects" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:opacity-70">
                <ArrowLeft size={12} /> Back to projects
            </Link>

            {/* Header */}
            <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="chip chip-navy capitalize">{(project.project_type || "").replace(/_/g, " ")}</div>
                    <h1 className="mt-3 font-display text-4xl tracking-tight leading-none italic" data-testid="project-name">{project.name}</h1>
                    <div className="mt-2 text-xs text-muted-foreground capitalize">
                        Status: {project.status} · Phase: {(project.current_phase_key || "—").replace(/_/g, " ")}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="overline">Progress</div>
                        <div className="font-display text-2xl">{Math.round(project.completion_pct || 0)}%</div>
                    </div>
                    <div className="text-right">
                        <div className="overline">Budget</div>
                        <div className="font-display text-2xl">{CURRENCY.format(project.total_budget || 0)}</div>
                    </div>
                </div>
            </div>

            {/* Tabs — horizontally scrollable on mobile so the 14 project tabs stay usable */}
            <div className="mt-6 flex flex-nowrap gap-1 border-b border-border overflow-x-auto -mx-6 md:mx-0 px-6 md:px-0" data-testid="project-tabs">
                {TABS.map((tt) => {
                    const Ic = tt.icon;
                    return (
                        <button key={tt.key} onClick={() => setTab(tt.key)}
                            className={`shrink-0 px-3 py-3 text-sm border-b-2 -mb-px inline-flex items-center gap-2 whitespace-nowrap ${tab === tt.key ? "border-navy text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                            data-testid={`tab-${tt.key}`}>
                            <Ic size={13} /> {tt.label}
                        </button>
                    );
                })}
            </div>

            <div className="mt-8">
                {tab === "overview" && <OverviewTab project={project} phases={phases} onRefresh={loadProject} />}
                {tab === "roadmap" && <RoadmapTab projectId={id} phases={phases} onRefresh={loadProject} />}
                {tab === "checklist" && <ChecklistTab projectId={id} phases={phases} />}
                {tab === "tasks" && <TasksTab projectId={id} phases={phases} onRefresh={loadProject} />}
                {tab === "schedule" && <ScheduleTab projectId={id} phases={phases} />}
                {tab === "field" && <FieldTab projectId={id} project={project} />}
                {tab === "budget" && <BudgetTab projectId={id} project={project} />}
                {tab === "capital" && <CapitalTab projectId={id} />}
                {tab === "coordination" && <CoordinationTab projectId={id} />}
                {tab === "reports" && <ReportsTab projectId={id} />}
                {tab === "permits" && <PermitsTab projectId={id} />}
                {tab === "inspections" && <InspectionsTab projectId={id} />}
                {tab === "team" && <TeamTab projectId={id} />}
                {tab === "files" && <RealFilesTab projectId={id} />}
                {tab === "activity" && <ActivityTab projectId={id} />}
            </div>
        </div>
    );
}

/* ------------------ Overview ------------------ */
function OverviewTab({ project, phases, onRefresh }) {
    const [tasks, setTasks] = useState([]);
    const [readiness, setReadiness] = useState(null);
    const [budgetSummary, setBudgetSummary] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: tk }, { data: rd }, { data: bs }] = await Promise.all([
                    api.get(`/projects/${project.id}/tasks`),
                    api.get(`/projects/${project.id}/funding-readiness`),
                    api.get(`/projects/${project.id}/budget/summary`),
                ]);
                setTasks(tk.items || []);
                setReadiness(rd);
                setBudgetSummary(bs);
            } catch { /* silent */ }
        })();
    }, [project.id]);

    const upcoming = useMemo(() => {
        return tasks.filter((t) => t.status !== "completed").sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")).slice(0, 8);
    }, [tasks]);

    return (
        <div className="space-y-8" data-testid="tab-overview-content">
            {/* AI Insights panel — deterministic + optional LLM narrative */}
            <AIInsightsPanel projectId={project.id} />

            {/* Phase timeline strip */}
            <div className="border border-border p-6">
                <div className="overline mb-4">Roadmap</div>
                <div className="flex items-center gap-1 overflow-x-auto pb-2" data-testid="overview-phase-strip">
                    {phases.map((ph, i) => {
                        const total = ph.task_count || 0;
                        const done = ph.task_done || 0;
                        const pct = total ? Math.round((done / total) * 100) : 0;
                        const status = pct === 100 ? "completed" : (done > 0 || ph.status === "in_progress") ? "in_progress" : "not_started";
                        return (
                            <div key={ph.id} className="min-w-[130px] flex-1 group" data-testid={`overview-phase-${ph.phase_key}`}>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Phase {i + 1}</div>
                                <div className={`h-1 mt-1 ${status === "completed" ? "bg-emerald-500" : status === "in_progress" ? "bg-steel" : "bg-secondary"}`} />
                                <div className="mt-2 text-xs font-medium truncate">{ph.name}</div>
                                <div className="text-[10px] text-muted-foreground">{done}/{total} tasks</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <StatCard label="Progress" value={`${Math.round(project.completion_pct || 0)}%`} sub="Complete" />
                <StatCard label="Budget" value={CURRENCY.format(budgetSummary?.forecast_at_completion || project.total_budget || 0)} sub="Forecast at completion" />
                <StatCard label="Funding readiness" value={`${Math.round(readiness?.readiness_pct || 0)}%`} sub={`${(readiness?.items || []).filter((i) => i.status === "complete").length}/${(readiness?.items || []).length} items`} />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-border p-6" data-testid="overview-upcoming">
                    <div className="flex items-center justify-between">
                        <div className="overline">Upcoming tasks</div>
                        <span className="text-[10px] text-muted-foreground">{upcoming.length} shown</span>
                    </div>
                    <ul className="mt-4 space-y-2">
                        {upcoming.length === 0 ? <li className="text-sm text-muted-foreground">No open tasks.</li> :
                            upcoming.map((t) => (
                                <li key={t.id} className="flex items-start gap-2 text-sm" data-testid={`overview-task-${t.id}`}>
                                    <Circle size={12} className="mt-1 text-muted-foreground" />
                                    <div className="flex-1">
                                        <div>{t.name}</div>
                                        <div className="text-[11px] text-muted-foreground">{t.due_date ? `Due ${t.due_date.slice(0, 10)}` : "No due date"} · {t.assigned_role || "unassigned"}</div>
                                    </div>
                                </li>
                            ))
                        }
                    </ul>
                </div>
                <div className="border border-border p-6" data-testid="overview-budget">
                    <div className="overline mb-3">Budget rollup</div>
                    {budgetSummary?.categories?.length ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={budgetSummary.categories}>
                                <XAxis dataKey="category" fontSize={10} />
                                <YAxis fontSize={10} />
                                <Tooltip formatter={(v) => CURRENCY.format(v)} />
                                <Bar dataKey="original" fill={NAVY} />
                                <Bar dataKey="forecast" fill={STEEL} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <div className="text-sm text-muted-foreground">No budget lines yet. Add lines in the Budget tab.</div>}
                    {budgetSummary && <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div><span className="overline block text-muted-foreground">Committed</span>{CURRENCY.format(budgetSummary.committed)}</div>
                        <div><span className="overline block text-muted-foreground">Actual</span>{CURRENCY.format(budgetSummary.actual)}</div>
                        <div><span className="overline block text-muted-foreground">CTC</span>{CURRENCY.format(budgetSummary.cost_to_complete)}</div>
                    </div>}
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, sub }) {
    return (
        <div className="border border-border p-5">
            <div className="overline">{label}</div>
            <div className="mt-2 font-display text-3xl italic tracking-tight">{value}</div>
            {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
        </div>
    );
}

/* ------------------ Roadmap ------------------ */
function RoadmapTab({ projectId, phases, onRefresh }) {
    const [openKey, setOpenKey] = useState(null);
    const [phaseTasks, setPhaseTasks] = useState({});
    const [phaseChecklist, setPhaseChecklist] = useState({});

    const expand = async (ph) => {
        if (openKey === ph.id) { setOpenKey(null); return; }
        setOpenKey(ph.id);
        if (!phaseTasks[ph.id]) {
            const { data } = await api.get(`/projects/${projectId}/tasks?phase_id=${ph.id}`);
            setPhaseTasks((prev) => ({ ...prev, [ph.id]: data.items || [] }));
        }
        if (!phaseChecklist[ph.id]) {
            const { data } = await api.get(`/projects/${projectId}/checklist?phase_id=${ph.id}`);
            setPhaseChecklist((prev) => ({ ...prev, [ph.id]: data.items || [] }));
        }
    };

    const toggleTask = async (task) => {
        const nextStatus = task.status === "completed" ? "not_started" : "completed";
        await api.patch(`/projects/${projectId}/tasks/${task.id}`, { status: nextStatus });
        setPhaseTasks((prev) => ({ ...prev, [task.phase_id]: prev[task.phase_id].map((t) => t.id === task.id ? { ...t, status: nextStatus } : t) }));
        onRefresh?.();
    };

    const updatePhase = async (phase, status) => {
        await api.patch(`/projects/${projectId}/phases/${phase.id}`, { status });
        toast.success(`Phase marked ${status.replace(/_/g, " ")}`);
        onRefresh?.();
    };

    return (
        <div className="space-y-3" data-testid="tab-roadmap-content">
            {phases.map((ph, i) => {
                const total = ph.task_count || 0;
                const done = ph.task_done || 0;
                const pct = total ? Math.round((done / total) * 100) : 0;
                const isOpen = openKey === ph.id;
                return (
                    <div key={ph.id} className={`border ${ph.is_gate ? "border-navy" : "border-border"}`} data-testid={`roadmap-phase-${ph.phase_key}`}>
                        <button onClick={() => expand(ph)} className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-secondary/40">
                            <div className="text-xs text-muted-foreground w-10">{String(i + 1).padStart(2, "0")}</div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium">{ph.name}</span>
                                    {ph.is_gate && <span className="chip chip-navy" data-testid="phase-gate-badge">Gate</span>}
                                </div>
                                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                                    <span>{done}/{total} tasks</span>
                                    <span className="capitalize">Status: {ph.status.replace(/_/g, " ")}</span>
                                </div>
                            </div>
                            <div className="w-32">
                                <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                                    <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-navy"}`} style={{ width: `${pct}%` }} />
                                </div>
                                <div className="mt-1 text-[10px] text-muted-foreground text-right">{pct}%</div>
                            </div>
                        </button>
                        {isOpen && (
                            <div className="px-5 pb-5 space-y-4 border-t border-border">
                                {ph.is_gate && (ph.gate_criteria || []).length > 0 && (
                                    <div className="mt-4 p-3 bg-secondary/40 text-xs">
                                        <div className="overline mb-2">Gate criteria</div>
                                        <ul className="space-y-1">
                                            {ph.gate_criteria.map((c, i2) => <li key={i2}>• {c}</li>)}
                                        </ul>
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">Update phase status:</span>
                                    {["not_started", "in_progress", "completed", "blocked"].map((s) => (
                                        <button key={s} onClick={() => updatePhase(ph, s)} className={`text-[11px] px-2 py-1 border ${ph.status === s ? "border-navy bg-secondary" : "border-border"}`} data-testid={`phase-status-${ph.phase_key}-${s}`}>{s.replace(/_/g, " ")}</button>
                                    ))}
                                </div>
                                <div>
                                    <div className="overline mb-2">Tasks</div>
                                    <ul className="divide-y divide-border border border-border">
                                        {(phaseTasks[ph.id] || []).map((t) => (
                                            <li key={t.id} className="px-3 py-2 flex items-center gap-3 text-sm hover:bg-secondary/40" data-testid={`roadmap-task-${t.id}`}>
                                                <button onClick={() => toggleTask(t)} className="shrink-0" data-testid={`toggle-task-${t.id}`}>
                                                    {t.status === "completed" ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Circle size={14} className="text-muted-foreground" />}
                                                </button>
                                                <div className="flex-1 min-w-0 truncate">{t.name}{t.is_milestone && <span className="ml-2 chip chip-ai">Milestone</span>}</div>
                                                <div className="text-[11px] text-muted-foreground shrink-0">{t.assigned_role || "—"}</div>
                                            </li>
                                        ))}
                                        {(!phaseTasks[ph.id] || phaseTasks[ph.id].length === 0) && <li className="px-3 py-4 text-xs text-muted-foreground">No tasks in this phase.</li>}
                                    </ul>
                                </div>
                                {(phaseChecklist[ph.id] || []).length > 0 && (
                                    <div>
                                        <div className="overline mb-2">Checklist</div>
                                        <ul className="grid md:grid-cols-2 gap-2">
                                            {phaseChecklist[ph.id].map((ci) => (
                                                <li key={ci.id} className="border border-border p-3 text-sm" data-testid={`roadmap-checklist-${ci.id}`}>
                                                    <div className="flex items-center gap-2">
                                                        {ci.status === "complete" || ci.status === "verified" ? <CheckCircle2 size={12} className="text-emerald-500" /> : <AlertCircle size={12} className="text-muted-foreground" />}
                                                        <span className="font-medium">{ci.name}</span>
                                                    </div>
                                                    {ci.why_it_matters && <div className="mt-1 text-[11px] text-muted-foreground">{ci.why_it_matters}</div>}
                                                    <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{ci.status.replace(/_/g, " ")}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/* ------------------ Checklist ------------------ */
function ChecklistTab({ projectId, phases }) {
    const [items, setItems] = useState([]);
    const [filterPhase, setFilterPhase] = useState("");
    const [filterStatus, setFilterStatus] = useState("");

    const load = async () => {
        const { data } = await api.get(`/projects/${projectId}/checklist`);
        setItems(data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    const filtered = items.filter((it) => (!filterPhase || it.phase_id === filterPhase) && (!filterStatus || it.status === filterStatus));

    const updateStatus = async (item, status) => {
        try {
            await api.patch(`/projects/${projectId}/checklist/${item.id}`, { status });
            setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, status } : i));
        } catch { toast.error("Update failed"); }
    };

    return (
        <div className="space-y-4" data-testid="tab-checklist-content">
            <div className="flex flex-wrap gap-3 items-center">
                <select value={filterPhase} onChange={(e) => setFilterPhase(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="checklist-phase-filter">
                    <option value="">All phases</option>
                    {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="checklist-status-filter">
                    <option value="">All statuses</option>
                    {CHECKLIST_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <span className="ml-auto text-xs text-muted-foreground">{filtered.length} items</span>
            </div>
            <div className="border border-border" data-testid="checklist-table">
                <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr" }}>
                    <div>Item</div><div>Phase</div><div>Responsible</div><div>Status</div>
                </div>
                {filtered.map((it) => {
                    const ph = phases.find((p) => p.id === it.phase_id);
                    return (
                        <div key={it.id} className="grid px-4 py-3 border-b border-border last:border-b-0 text-sm items-center" style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr" }} data-testid={`checklist-row-${it.id}`}>
                            <div>
                                <div>{it.name}</div>
                                {it.why_it_matters && <div className="text-[11px] text-muted-foreground mt-0.5">{it.why_it_matters}</div>}
                            </div>
                            <div className="text-xs text-muted-foreground">{ph?.name}</div>
                            <div className="text-xs">{it.responsible_role || "—"}</div>
                            <select value={it.status} onChange={(e) => updateStatus(it, e.target.value)} className="border border-border bg-background px-2 py-1 text-xs" data-testid={`checklist-status-${it.id}`}>
                                {CHECKLIST_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                            </select>
                        </div>
                    );
                })}
                {filtered.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No checklist items match.</div>}
            </div>
        </div>
    );
}

/* ------------------ Tasks ------------------ */
function TasksTab({ projectId, phases, onRefresh }) {
    const [tasks, setTasks] = useState([]);
    const [drawerTask, setDrawerTask] = useState(null);
    const [adding, setAdding] = useState(false);
    const [newTask, setNewTask] = useState({ name: "", phase_id: phases[0]?.id || "" });

    const load = async () => {
        const { data } = await api.get(`/projects/${projectId}/tasks`);
        setTasks(data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    const updateStatus = async (task, status) => {
        try {
            await api.patch(`/projects/${projectId}/tasks/${task.id}`, { status });
            setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status } : t));
            onRefresh?.();
        } catch { toast.error("Update failed"); }
    };

    const deleteTask = async (task) => {
        if (!window.confirm(`Delete task "${task.name}"?`)) return;
        await api.delete(`/projects/${projectId}/tasks/${task.id}`);
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
        setDrawerTask(null);
        onRefresh?.();
    };

    const addTask = async (e) => {
        e.preventDefault();
        if (!newTask.name || !newTask.phase_id) return;
        try {
            const { data } = await api.post(`/projects/${projectId}/tasks`, newTask);
            setTasks((prev) => [...prev, data]);
            setNewTask({ name: "", phase_id: phases[0]?.id || "" });
            setAdding(false);
            onRefresh?.();
            toast.success("Task added");
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="space-y-4" data-testid="tab-tasks-content">
            <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{tasks.length} tasks</span>
                <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-navy text-arch-white px-3 py-1.5 text-xs hover:opacity-90" data-testid="add-task-btn">
                    <Plus size={12} /> New task
                </button>
            </div>
            {adding && (
                <form onSubmit={addTask} className="border border-border p-4 flex flex-wrap gap-2 items-end" data-testid="add-task-form">
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Task name</label>
                        <input value={newTask.name} onChange={(e) => setNewTask({ ...newTask, name: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="new-task-name" required />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Phase</label>
                        <select value={newTask.phase_id} onChange={(e) => setNewTask({ ...newTask, phase_id: e.target.value })} className="border border-border bg-background px-3 py-2 text-sm" data-testid="new-task-phase">
                            {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <button type="submit" className="bg-navy text-arch-white px-3 py-2 text-sm" data-testid="save-new-task">Save</button>
                    <button type="button" onClick={() => setAdding(false)} className="border border-border px-3 py-2 text-sm">Cancel</button>
                </form>
            )}
            <div className="border border-border">
                <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr" }}>
                    <div>Task</div><div>Phase</div><div>Role</div><div>Due</div><div>Status</div>
                </div>
                {tasks.map((t) => {
                    const ph = phases.find((p) => p.id === t.phase_id);
                    return (
                        <button key={t.id} onClick={() => setDrawerTask(t)} className="w-full grid px-4 py-3 border-b border-border last:border-b-0 text-sm text-left hover:bg-secondary/40 items-center" style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr" }} data-testid={`task-row-${t.id}`}>
                            <div>
                                {t.is_milestone && <span className="chip chip-ai mr-2">◆</span>}
                                {t.name}
                            </div>
                            <div className="text-xs text-muted-foreground">{ph?.name}</div>
                            <div className="text-xs">{t.assigned_role || "—"}</div>
                            <div className="text-xs">{t.due_date ? t.due_date.slice(0, 10) : "—"}</div>
                            <div className={`text-xs ${TASK_STATUS_COLORS[t.status]}`}>{t.status.replace(/_/g, " ")}</div>
                        </button>
                    );
                })}
                {tasks.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No tasks.</div>}
            </div>

            {drawerTask && <TaskDrawer task={drawerTask} phases={phases} projectId={projectId} onClose={() => setDrawerTask(null)} onUpdate={(updated) => { setTasks((prev) => prev.map((t) => t.id === updated.id ? updated : t)); setDrawerTask(updated); onRefresh?.(); }} onDelete={() => deleteTask(drawerTask)} />}
        </div>
    );
}

function TaskDrawer({ task, phases, projectId, onClose, onUpdate, onDelete }) {
    const [form, setForm] = useState({ ...task });
    const save = async () => {
        try {
            const { data } = await api.patch(`/projects/${projectId}/tasks/${task.id}`, {
                name: form.name, description: form.description, status: form.status,
                assigned_role: form.assigned_role, due_date: form.due_date, is_milestone: form.is_milestone,
            });
            toast.success("Saved");
            onUpdate?.(data);
        } catch { toast.error("Save failed"); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" data-testid="task-drawer">
            <div className="w-full max-w-lg bg-background border-l border-border h-full overflow-y-auto">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <div className="overline">Task</div>
                    <button onClick={onClose} className="text-muted-foreground hover:opacity-70" data-testid="close-drawer"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="overline block mb-1">Name</label>
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="drawer-name" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Description</label>
                        <textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="drawer-desc" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-1">Status</label>
                            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="drawer-status">
                                {TASK_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="overline block mb-1">Role</label>
                            <input value={form.assigned_role || ""} onChange={(e) => setForm({ ...form, assigned_role: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="drawer-role" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Due date</label>
                            <input type="date" value={(form.due_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="drawer-due" />
                        </div>
                        <div className="flex items-end">
                            <label className="text-sm inline-flex items-center gap-2">
                                <input type="checkbox" checked={!!form.is_milestone} onChange={(e) => setForm({ ...form, is_milestone: e.target.checked })} data-testid="drawer-milestone" />
                                Milestone
                            </label>
                        </div>
                    </div>
                    {(task.predecessor_ids || []).length > 0 && (
                        <div>
                            <label className="overline block mb-1">Dependencies</label>
                            <div className="text-xs text-muted-foreground">{task.predecessor_ids.length} predecessor task(s)</div>
                        </div>
                    )}
                </div>
                <div className="px-5 py-4 border-t border-border flex items-center justify-between">
                    <button onClick={onDelete} className="text-destructive text-xs inline-flex items-center gap-1 hover:opacity-70" data-testid="drawer-delete"><Trash2 size={12} /> Delete</button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="border border-border px-3 py-1.5 text-sm">Close</button>
                        <button onClick={save} className="bg-navy text-arch-white px-4 py-1.5 text-sm" data-testid="drawer-save">Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ------------------ Schedule ------------------ */
function ScheduleTab({ projectId, phases }) {
    const [tasks, setTasks] = useState([]);
    const [view, setView] = useState("list");

    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/projects/${projectId}/tasks`);
            setTasks(data.items || []);
        })();
    }, [projectId]);

    return (
        <div className="space-y-4" data-testid="tab-schedule-content">
            <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">View:</span>
                {["list", "gantt", "kanban", "calendar"].map((v) => (
                    <button key={v} onClick={() => setView(v)} className={`text-xs px-3 py-1.5 border ${view === v ? "border-navy bg-secondary" : "border-border"}`} data-testid={`schedule-view-${v}`}>{v}</button>
                ))}
            </div>
            {view === "list" && (
                <div className="space-y-4">
                    {phases.map((ph) => {
                        const ptasks = tasks.filter((t) => t.phase_id === ph.id);
                        if (!ptasks.length) return null;
                        return (
                            <div key={ph.id} className="border border-border" data-testid={`schedule-phase-${ph.phase_key}`}>
                                <div className="px-4 py-2 border-b border-border bg-secondary/40 text-sm font-medium">{ph.name}</div>
                                {ptasks.map((t) => (
                                    <div key={t.id} className="grid px-4 py-2 border-b border-border last:border-b-0 text-xs" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr" }}>
                                        <div>{t.name}</div>
                                        <div>{(t.start_date || "").slice(0, 10)}</div>
                                        <div>{(t.due_date || "").slice(0, 10)}</div>
                                        <div className={TASK_STATUS_COLORS[t.status]}>{t.status.replace(/_/g, " ")}</div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
            {view === "gantt" && <GanttView phases={phases} tasks={tasks} />}
            {view === "kanban" && <KanbanView tasks={tasks} projectId={projectId} onUpdate={(id, patch) => setTasks((prev) => prev.map((t) => t.id === id ? { ...t, ...patch } : t))} />}
            {view === "calendar" && <CalendarView tasks={tasks} />}
        </div>
    );
}

function GanttView({ phases, tasks }) {
    // Compute overall date range
    const dated = tasks.filter((t) => t.start_date && t.due_date);
    if (!dated.length) return <div className="text-sm text-muted-foreground">No dated tasks.</div>;
    const min = new Date(Math.min(...dated.map((t) => new Date(t.start_date).getTime())));
    const max = new Date(Math.max(...dated.map((t) => new Date(t.due_date).getTime())));
    const totalMs = Math.max(1, max - min);
    const totalDays = Math.max(30, Math.ceil(totalMs / (1000 * 3600 * 24)));

    return (
        <div className="border border-border p-4 overflow-x-auto" data-testid="gantt-view">
            <div className="text-xs text-muted-foreground mb-3">Range: {min.toISOString().slice(0, 10)} → {max.toISOString().slice(0, 10)} ({totalDays} days)</div>
            <div className="space-y-1 min-w-[900px]">
                {phases.map((ph) => (
                    <React.Fragment key={ph.id}>
                        <div className="text-xs font-medium mt-3 mb-1">{ph.name}</div>
                        {tasks.filter((t) => t.phase_id === ph.id && t.start_date && t.due_date).map((t) => {
                            const s = new Date(t.start_date);
                            const e = new Date(t.due_date);
                            const leftPct = ((s - min) / totalMs) * 100;
                            const widthPct = Math.max(0.5, ((e - s) / totalMs) * 100);
                            const color = t.is_milestone ? ICE : (t.status === "completed" ? "#10b981" : NAVY);
                            return (
                                <div key={t.id} className="relative h-6 bg-secondary/40" data-testid={`gantt-bar-${t.id}`}>
                                    <div className="absolute top-0 h-full flex items-center px-2 text-[10px] text-arch-white overflow-hidden" style={{ left: `${leftPct}%`, width: `${widthPct}%`, background: color, minWidth: 40 }}>
                                        <span className="truncate">{t.name}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

function KanbanView({ tasks, projectId, onUpdate }) {
    const moveTo = async (task, status) => {
        try {
            await api.patch(`/projects/${projectId}/tasks/${task.id}`, { status });
            onUpdate?.(task.id, { status });
        } catch { toast.error("Update failed"); }
    };
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kanban-view">
            {TASK_STATUSES.map((s) => {
                const items = tasks.filter((t) => t.status === s);
                return (
                    <div key={s} className="border border-border" data-testid={`kanban-col-${s}`}>
                        <div className="px-3 py-2 border-b border-border bg-secondary/40 text-xs font-medium capitalize">{s.replace(/_/g, " ")} ({items.length})</div>
                        <div className="p-2 space-y-2 max-h-[500px] overflow-y-auto">
                            {items.map((t) => (
                                <div key={t.id} className="border border-border p-2 text-xs bg-background" data-testid={`kanban-card-${t.id}`}>
                                    <div>{t.name}</div>
                                    <div className="mt-1 flex items-center gap-1">
                                        {TASK_STATUSES.filter((x) => x !== s).map((x) => (
                                            <button key={x} onClick={() => moveTo(t, x)} className="text-[9px] px-1.5 py-0.5 border border-border hover:bg-secondary" data-testid={`kanban-move-${t.id}-${x}`}>{x.slice(0, 4)}</button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function CalendarView({ tasks }) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth());
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = firstDay.getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const byDay = {};
    tasks.forEach((t) => {
        if (!t.due_date) return;
        const dt = new Date(t.due_date);
        if (dt.getFullYear() === year && dt.getMonth() === month) {
            const d = dt.getDate();
            (byDay[d] = byDay[d] || []).push(t);
        }
    });

    return (
        <div className="border border-border" data-testid="calendar-view">
            <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                <button onClick={() => { const d = new Date(year, month - 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }} className="text-xs">← Prev</button>
                <div className="text-sm font-medium">{firstDay.toLocaleString("en-US", { month: "long", year: "numeric" })}</div>
                <button onClick={() => { const d = new Date(year, month + 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }} className="text-xs">Next →</button>
            </div>
            <div className="grid grid-cols-7 border-b border-border">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="text-[10px] uppercase p-2 text-center text-muted-foreground">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
                {cells.map((d, i) => (
                    <div key={i} className="min-h-[80px] border-b border-r border-border p-1 text-[10px]">
                        {d && <>
                            <div className="text-muted-foreground">{d}</div>
                            <div className="mt-1 space-y-0.5">
                                {(byDay[d] || []).slice(0, 3).map((t) => (
                                    <div key={t.id} className="px-1 py-0.5 bg-navy text-arch-white truncate" title={t.name}>{t.name}</div>
                                ))}
                                {(byDay[d] || []).length > 3 && <div className="text-muted-foreground">+{byDay[d].length - 3} more</div>}
                            </div>
                        </>}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ------------------ Budget ------------------ */
function BudgetTab({ projectId, project }) {
    const [lines, setLines] = useState([]);
    const [summary, setSummary] = useState(null);
    const [codes, setCodes] = useState([]);
    const [readiness, setReadiness] = useState(null);
    const [draws, setDraws] = useState([]);
    const [sub, setSub] = useState("budget"); // budget | funding

    const load = async () => {
        const [{ data: L }, { data: S }, { data: C }, { data: R }, { data: D }] = await Promise.all([
            api.get(`/projects/${projectId}/budget-lines`),
            api.get(`/projects/${projectId}/budget/summary`),
            api.get(`/cost-codes`),
            api.get(`/projects/${projectId}/funding-readiness`),
            api.get(`/projects/${projectId}/draws`),
        ]);
        setLines(L.items || []); setSummary(S); setCodes(C.items || []);
        setReadiness(R); setDraws(D.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    return (
        <div className="space-y-6" data-testid="tab-budget-content">
            <div className="flex items-center gap-2">
                <button onClick={() => setSub("budget")} className={`text-xs px-3 py-1.5 border ${sub === "budget" ? "border-navy bg-secondary" : "border-border"}`} data-testid="budget-sub-budget">Budget</button>
                <button onClick={() => setSub("funding")} className={`text-xs px-3 py-1.5 border ${sub === "funding" ? "border-navy bg-secondary" : "border-border"}`} data-testid="budget-sub-funding">Funding</button>
            </div>
            {sub === "budget" ? (
                <>
                    <BudgetSummaryPanel summary={summary} />
                    <BudgetLinesTable projectId={projectId} lines={lines} codes={codes} onReload={load} />
                </>
            ) : (
                <>
                    <FundingReadinessPanel projectId={projectId} readiness={readiness} onReload={load} />
                    <DrawsRegister projectId={projectId} draws={draws} onReload={load} />
                </>
            )}
        </div>
    );
}

function BudgetSummaryPanel({ summary }) {
    if (!summary) return null;
    return (
        <div className="border border-border p-6" data-testid="budget-summary">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <SumStat label="Original" value={CURRENCY.format(summary.original_budget)} />
                <SumStat label="Committed" value={CURRENCY.format(summary.committed)} />
                <SumStat label="Actual" value={CURRENCY.format(summary.actual)} />
                <SumStat label="Forecast" value={CURRENCY.format(summary.forecast_at_completion)} />
                <SumStat label="Variance" value={CURRENCY.format(summary.variance_vs_original)} tint={summary.variance_vs_original > 0 ? "text-destructive" : "text-emerald-500"} />
                <SumStat label="Cost to Complete" value={CURRENCY.format(summary.cost_to_complete)} />
            </div>
            {summary.categories?.length > 0 && (
                <div className="mt-6">
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={summary.categories}>
                            <XAxis dataKey="category" fontSize={10} />
                            <YAxis fontSize={10} />
                            <Tooltip formatter={(v) => CURRENCY.format(v)} />
                            <Bar dataKey="original" fill={NAVY} name="Original" />
                            <Bar dataKey="committed" fill={STEEL} name="Committed" />
                            <Bar dataKey="actual" fill="#10b981" name="Actual" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

function SumStat({ label, value, tint }) {
    return (
        <div>
            <div className="overline">{label}</div>
            <div className={`mt-1 font-display text-xl italic ${tint || ""}`}>{value}</div>
        </div>
    );
}

function BudgetLinesTable({ projectId, lines, codes, onReload }) {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ category: "Hard Costs", name: "", original_amount: 0 });

    const addLine = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/projects/${projectId}/budget-lines`, form);
            setForm({ category: "Hard Costs", name: "", original_amount: 0 });
            setAdding(false); onReload?.();
            toast.success("Line added");
        } catch { toast.error("Failed"); }
    };

    const updateField = async (line, field, value) => {
        try {
            await api.patch(`/projects/${projectId}/budget-lines/${line.id}`, { [field]: parseFloat(value) || 0 });
            onReload?.();
        } catch { toast.error("Update failed"); }
    };

    const deleteLine = async (line) => {
        if (!window.confirm("Delete line?")) return;
        await api.delete(`/projects/${projectId}/budget-lines/${line.id}`);
        onReload?.();
    };

    const categories = Array.from(new Set(codes.map((c) => c.category)));

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="overline">Line items ({lines.length})</div>
                <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-navy text-arch-white px-3 py-1.5 text-xs" data-testid="add-budget-line">
                    <Plus size={12} /> Add line
                </button>
            </div>
            {adding && (
                <form onSubmit={addLine} className="border border-border p-4 mb-3 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="add-line-form">
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-border bg-background px-3 py-2 text-sm" data-testid="line-category">
                        {(categories.length ? categories : ["Land", "Hard Costs", "Soft Costs", "Financing", "Contingency"]).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input placeholder="Line name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-border bg-background px-3 py-2 text-sm" data-testid="line-name" required />
                    <input type="number" placeholder="Original amount" value={form.original_amount} onChange={(e) => setForm({ ...form, original_amount: parseFloat(e.target.value) || 0 })} className="border border-border bg-background px-3 py-2 text-sm" data-testid="line-amount" required />
                    <div className="flex gap-1">
                        <button type="submit" className="bg-navy text-arch-white px-3 py-2 text-sm flex-1" data-testid="save-line">Save</button>
                        <button type="button" onClick={() => setAdding(false)} className="border border-border px-3 py-2 text-sm">×</button>
                    </div>
                </form>
            )}
            <div className="border border-border" data-testid="budget-lines-table">
                <div className="grid px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "1.2fr 2fr 1fr 1fr 1fr 1fr 1fr 30px" }}>
                    <div>Category</div><div>Line</div><div>Original</div><div>Committed</div><div>Actual</div><div>Forecast</div><div>Variance</div><div></div>
                </div>
                {lines.map((l) => (
                    <div key={l.id} className="grid px-3 py-2 border-b border-border last:border-b-0 text-xs items-center" style={{ gridTemplateColumns: "1.2fr 2fr 1fr 1fr 1fr 1fr 1fr 30px" }} data-testid={`budget-line-${l.id}`}>
                        <div className="text-muted-foreground">{l.category}</div>
                        <div>{l.name}</div>
                        <input type="number" defaultValue={l.original_amount} onBlur={(e) => updateField(l, "original_amount", e.target.value)} className="border border-border bg-transparent px-2 py-1 text-xs w-full" data-testid={`line-original-${l.id}`} />
                        <input type="number" defaultValue={l.committed_amount} onBlur={(e) => updateField(l, "committed_amount", e.target.value)} className="border border-border bg-transparent px-2 py-1 text-xs w-full" data-testid={`line-committed-${l.id}`} />
                        <input type="number" defaultValue={l.actual_amount} onBlur={(e) => updateField(l, "actual_amount", e.target.value)} className="border border-border bg-transparent px-2 py-1 text-xs w-full" data-testid={`line-actual-${l.id}`} />
                        <div>{CURRENCY.format(l.forecast_at_completion || 0)}</div>
                        <div className={l.variance > 0 ? "text-destructive" : "text-emerald-500"}>{CURRENCY.format(l.variance || 0)}</div>
                        <button onClick={() => deleteLine(l)} className="text-destructive" data-testid={`delete-line-${l.id}`}><Trash2 size={12} /></button>
                    </div>
                ))}
                {lines.length === 0 && <div className="px-3 py-6 text-sm text-muted-foreground">No budget lines yet.</div>}
            </div>
        </div>
    );
}

function FundingReadinessPanel({ projectId, readiness, onReload }) {
    if (!readiness) return null;
    const total = readiness.items.length;
    const done = readiness.items.filter((i) => i.status === "complete" || i.status === "na").length;

    const update = async (item, status) => {
        try {
            await api.patch(`/projects/${projectId}/funding-readiness`, { item_key: item.key, status });
            onReload?.();
        } catch { toast.error("Update failed"); }
    };

    return (
        <div className="border border-border p-6" data-testid="funding-readiness-panel">
            <div className="flex items-center justify-between">
                <div className="overline">Funding Readiness · {done}/{total} complete</div>
                <div className="font-display text-2xl italic">{Math.round(readiness.readiness_pct)}%</div>
            </div>
            <div className="mt-3 h-1.5 bg-secondary rounded-sm overflow-hidden">
                <div className="h-full bg-navy" style={{ width: `${readiness.readiness_pct}%` }} />
            </div>
            <ul className="mt-5 divide-y divide-border border border-border">
                {readiness.items.map((it) => (
                    <li key={it.key} className="grid px-3 py-2 text-sm items-center" style={{ gridTemplateColumns: "3fr 1fr" }} data-testid={`readiness-${it.key}`}>
                        <div>
                            <div>{it.name}</div>
                            {it.required_document_types?.length > 0 && <div className="text-[10px] text-muted-foreground">Docs: {it.required_document_types.join(", ")}</div>}
                        </div>
                        <select value={it.status} onChange={(e) => update(it, e.target.value)} className="border border-border bg-background px-2 py-1 text-xs" data-testid={`readiness-status-${it.key}`}>
                            {["not_started", "in_progress", "complete", "na"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                        </select>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function DrawsRegister({ projectId, draws, onReload }) {
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ requested_amount: 0, notes: "" });

    const createDraw = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/projects/${projectId}/draws`, form);
            setForm({ requested_amount: 0, notes: "" });
            setCreating(false); onReload?.();
            toast.success("Draw request created");
        } catch { toast.error("Failed"); }
    };

    const updateStatus = async (draw, status) => {
        await api.patch(`/projects/${projectId}/draws/${draw.id}`, { status });
        onReload?.();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="overline">Draw Register ({draws.length})</div>
                <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-navy text-arch-white px-3 py-1.5 text-xs" data-testid="create-draw-btn">
                    <Plus size={12} /> Create draw request
                </button>
            </div>
            {creating && (
                <form onSubmit={createDraw} className="border border-border p-4 mb-3 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="draw-form">
                    <input type="number" placeholder="Requested amount" value={form.requested_amount} onChange={(e) => setForm({ ...form, requested_amount: parseFloat(e.target.value) || 0 })} className="border border-border bg-background px-3 py-2 text-sm" data-testid="draw-amount" required />
                    <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-border bg-background px-3 py-2 text-sm md:col-span-1" data-testid="draw-notes" />
                    <div className="flex gap-1">
                        <button type="submit" className="bg-navy text-arch-white px-3 py-2 text-sm flex-1" data-testid="save-draw">Save</button>
                        <button type="button" onClick={() => setCreating(false)} className="border border-border px-3 py-2 text-sm">×</button>
                    </div>
                </form>
            )}
            <div className="border border-border" data-testid="draws-table">
                <div className="grid px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "40px 1fr 1fr 1fr 1fr 1fr" }}>
                    <div>#</div><div>Requested</div><div>Approved</div><div>Funded</div><div>Status</div><div>Date</div>
                </div>
                {draws.map((d) => (
                    <div key={d.id} className="grid px-3 py-2 border-b border-border last:border-b-0 text-xs items-center" style={{ gridTemplateColumns: "40px 1fr 1fr 1fr 1fr 1fr" }} data-testid={`draw-row-${d.id}`}>
                        <div>#{d.draw_number}</div>
                        <div>{CURRENCY.format(d.requested_amount)}</div>
                        <div>{CURRENCY.format(d.approved_amount || 0)}</div>
                        <div>{CURRENCY.format(d.funded_amount || 0)}</div>
                        <select value={d.status} onChange={(e) => updateStatus(d, e.target.value)} className="border border-border bg-transparent px-2 py-1 text-xs" data-testid={`draw-status-${d.id}`}>
                            {["draft", "missing_documents", "ready", "submitted", "under_review", "approved", "partially_funded", "funded", "rejected"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                        </select>
                        <div className="text-muted-foreground">{(d.request_date || "").slice(0, 10)}</div>
                    </div>
                ))}
                {draws.length === 0 && <div className="px-3 py-6 text-sm text-muted-foreground">No draws yet.</div>}
            </div>
        </div>
    );
}

/* ------------------ Team ------------------ */
function TeamTab({ projectId }) {
    const [team, setTeam] = useState([]);
    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/projects/${projectId}/team`);
            setTeam(data.items || []);
        })();
    }, [projectId]);
    return (
        <div className="border border-border" data-testid="tab-team-content">
            <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "2fr 2fr 1fr" }}>
                <div>Name</div><div>Email</div><div>Role</div>
            </div>
            {team.map((m) => (
                <div key={m.user_id} className="grid px-4 py-3 border-b border-border last:border-b-0 text-sm" style={{ gridTemplateColumns: "2fr 2fr 1fr" }} data-testid={`team-row-${m.user_id}`}>
                    <div>{m.first_name} {m.last_name}</div>
                    <div className="text-muted-foreground">{m.email}</div>
                    <div className="text-xs capitalize">{(m.role_key || "").replace(/_/g, " ")}</div>
                </div>
            ))}
            {team.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No team members. Invite via Settings → Users.</div>}
        </div>
    );
}

/* ------------------ Activity ------------------ */
function ActivityTab({ projectId }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/projects/${projectId}/activity`);
            setRows(data.items || []);
        })();
    }, [projectId]);
    return (
        <div className="border border-border" data-testid="tab-activity-content">
            <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "1fr 2fr 2fr 1fr" }}>
                <div>Time</div><div>Action</div><div>Target</div><div>Actor</div>
            </div>
            {rows.map((a) => (
                <div key={a.id} className="grid px-4 py-2 border-b border-border last:border-b-0 text-xs" style={{ gridTemplateColumns: "1fr 2fr 2fr 1fr" }} data-testid={`activity-row-${a.id}`}>
                    <div className="text-muted-foreground">{(a.timestamp || "").slice(0, 19).replace("T", " ")}</div>
                    <div>{a.action}</div>
                    <div className="text-muted-foreground">{a.target} · {a.target_id ? a.target_id.slice(0, 8) : "—"}</div>
                    <div className="text-muted-foreground">{(a.actor_user_id || "").slice(0, 8)}</div>
                </div>
            ))}
            {rows.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No activity yet.</div>}
        </div>
    );
}
